"use client";

import { useCallback, useEffect } from "react";

import {
  clampChatWidth,
  useAskFocalSidebarStore,
} from "@/store/askFocalSidebar";

/** How far an arrow key nudges the handle. */
const KEYBOARD_STEP = 16;

interface UseChatPanelResizeOptions {
  /**
   * Run once the drag ends, with the width it settled on. The conversation
   * sidebar uses this to retract the main sidebar when the chat has taken over
   * a narrow viewport; nothing else needs it.
   */
  onDragEnd?: (finalWidth: number) => void;
}

interface ChatPanelResize {
  /** The panel's current width, already clamped to the chat's range. */
  width: number;
  isDragging: boolean;
  /** Spread onto the handle element. */
  handleProps: {
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
    onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
    onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
  };
}

/**
 * Drives the drag handle on a chat panel's left edge.
 *
 * Width is read from and written to the shared `askFocalSidebar` store rather
 * than local state, for two reasons: the conversation sidebar's width and the
 * deliverable rail's width are one preference, so sizing one sizes the other;
 * and MainLayout's `padding-right` reads the same value, so the content edge
 * tracks the cursor on the same frame instead of a render behind it.
 *
 * The drag runs on **pointer capture**, not on document-level mouse listeners.
 * The handle owns the pointer for the whole gesture, so the move events keep
 * arriving even while the cursor is over something that would otherwise eat
 * them — which on the deliverable viewer it always is, because widening the
 * chat drags the cursor straight across the document preview's `<iframe>`, and
 * an iframe delivers mousemove to its own document rather than to ours. It
 * also means the gesture cannot be stranded by an unmount mid-drag, so nothing
 * here has to remember a listener in order to remove it later.
 *
 * Both panels are anchored to the right with the handle on their left edge, so
 * ArrowLeft widens and ArrowRight narrows.
 */
export const useChatPanelResize = ({
  onDragEnd,
}: UseChatPanelResizeOptions = {}): ChatPanelResize => {
  const storedPanelWidth = useAskFocalSidebarStore((s) => s.panelWidth);
  const isDragging = useAskFocalSidebarStore((s) => s.isDragging);
  const setIsDragging = useAskFocalSidebarStore((s) => s.setIsDragging);
  const setPanelWidth = useAskFocalSidebarStore((s) => s.setPanelWidth);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      setIsDragging(true);
    },
    [setIsDragging],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
      setPanelWidth(clampChatWidth(window.innerWidth - e.clientX));
    },
    [setPanelWidth],
  );

  // Deliberately not gated on `hasPointerCapture`: engines differ on whether
  // the implicit release lands before or after `pointerup` is dispatched, and
  // a guard that loses that race leaves the drag flag — and with it the
  // `ew-resize` cursor on <body> — stuck on for the rest of the session. The
  // store tells us whether a drag was really in progress, which is the thing
  // `onDragEnd` actually cares about.
  const endDrag = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      const { isDragging: wasDragging, panelWidth } =
        useAskFocalSidebarStore.getState();
      if (!wasDragging) return;
      setIsDragging(false);
      onDragEnd?.(panelWidth);
    },
    [setIsDragging, onDragEnd],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      let delta = 0;
      if (e.key === "ArrowLeft") delta = KEYBOARD_STEP;
      else if (e.key === "ArrowRight") delta = -KEYBOARD_STEP;
      else return;
      e.preventDefault();
      const current = useAskFocalSidebarStore.getState().panelWidth;
      setPanelWidth(clampChatWidth(current + delta));
    },
    [setPanelWidth],
  );

  // The resize cursor and the text-selection block belong to the gesture, not
  // to the handle, so they go on <body> for as long as the drag lasts. Keyed
  // off the same flag that ends the drag, so an unmount mid-drag cleans up.
  //
  // Set and cleared outright rather than saved and restored. More than one
  // panel can call this hook on the same screen — the conversation sidebar
  // lives in MainLayout on every page, including the one the deliverable rail
  // is on — and they all watch the same `isDragging`. A save/restore pair has
  // the second instance capturing the value the first just wrote, so whichever
  // cleanup happens to run last puts `ew-resize` back and the cursor stays
  // stuck for the rest of the session. Clearing is idempotent, so it does not
  // matter how many instances run it or in what order.
  useEffect(() => {
    if (!isDragging) return;
    const { body } = document;
    body.style.cursor = "ew-resize";
    body.style.userSelect = "none";
    return () => {
      body.style.removeProperty("cursor");
      body.style.removeProperty("user-select");
    };
  }, [isDragging]);

  return {
    width: clampChatWidth(storedPanelWidth),
    isDragging,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onKeyDown,
    },
  };
};
